import React from 'react';
import { useTranslation } from 'react-i18next';
import './PieChart.css';

export interface PieSlice {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  slices: PieSlice[];
  /** Label shown in the donut center below the total number */
  centerLabel?: string;
  /** Outer radius of the donut ring. Default 48 */
  radius?: number;
  /** Thickness of the ring as a fraction of radius. Default 0.22 */
  thickness?: number;
  /** When provided, arcs + legend rows become clickable (drilldown). */
  onSliceClick?: (slice: PieSlice, index: number) => void;
}

const PieChart: React.FC<PieChartProps> = ({
  slices,
  centerLabel = 'total',
  radius = 48,
  thickness = 0.22,
  onSliceClick,
}) => {
  const { t } = useTranslation();
  const clickable = typeof onSliceClick === 'function';
  const strokeW = Math.round(radius * thickness);
  const r = radius - strokeW / 2 - 2; // circle radius so stroke stays inside viewBox
  const circumference = 2 * Math.PI * r;
  const pad = strokeW / 2 + 2;
  const size = radius * 2 + pad * 2;
  const cx = size / 2;
  const cy = size / 2;

  const total = slices.reduce((s, sl) => s + sl.value, 0);

  // Build stroke segments: each gets a dasharray + dashoffset offset
  let offsetFraction = 0; // starts at top (-90°)
  const segments = slices
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.value > 0)
    .map(({ s, i }) => {
      const fraction = s.value / total;
      const dash = fraction * circumference;
      // gap = rest of circumference, offset rotates the segment into position
      const dasharray = `${dash - 1} ${circumference - dash + 1}`;
      const dashoffset = -(offsetFraction * circumference) + circumference * 0.25; // start at top
      offsetFraction += fraction;
      return { ...s, index: i, dasharray, dashoffset };
    });

  return (
    <div className="pc-wrap">
      <svg className="pc-svg" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {/* background track */}
        <circle
          cx={cx} cy={cy} r={r}
          style={{ fill: 'none', stroke: 'var(--border)', strokeWidth: strokeW }}
        />
        {total === 0 ? null : segments.map((seg, i) => (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            className={clickable ? 'pc-arc-clickable' : undefined}
            onClick={clickable ? () => onSliceClick!({ label: seg.label, value: seg.value, color: seg.color }, seg.index) : undefined}
            style={{
              fill: 'none',
              stroke: seg.color,
              strokeWidth: strokeW,
              strokeDasharray: seg.dasharray,
              strokeDashoffset: seg.dashoffset,
              opacity: 0.9,
              transition: 'stroke-dasharray 0.4s ease',
              cursor: clickable ? 'pointer' : undefined,
            }}
          >
            <title>{seg.label}: {seg.value}{clickable ? t('components.pieChart.clickToDrillDown') : ''}</title>
          </circle>
        ))}
        {/* center labels */}
        <text x={cx} y={cy - 4} className="pc-center-num">{total}</text>
        <text x={cx} y={cy + 11} className="pc-center-lbl">{centerLabel}</text>
      </svg>

      <div className="pc-legend">
        {slices.map((s, i) => (
          <div
            key={s.label}
            className={`pc-legend-row${clickable && s.value > 0 ? ' pc-legend-clickable' : ''}`}
            role={clickable && s.value > 0 ? 'button' : undefined}
            tabIndex={clickable && s.value > 0 ? 0 : undefined}
            onClick={clickable && s.value > 0 ? () => onSliceClick!(s, i) : undefined}
            onKeyDown={clickable && s.value > 0 ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSliceClick!(s, i); } } : undefined}
          >
            <span className="pc-dot" style={{ backgroundColor: s.color }} />
            <span className="pc-legend-label">{s.label}</span>
            <span className="pc-legend-val">{s.value}</span>
            <span className="pc-legend-pct">
              {total > 0 ? Math.round((s.value / total) * 100) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PieChart;
